'use client';

import { useEffect, useState, useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Paper } from '@mui/material';
import { useInterval } from '@scorenco/components';
import { AnimatePresence, motion, type HTMLMotionProps } from 'framer-motion';

type Sponsor = { logo?: string | null };

type GetSquarePartnersData = {
  sponsors_sponsor: Sponsor[];
};

const MotionImg = motion.img as React.ComponentType<
  React.ImgHTMLAttributes<HTMLImageElement> & HTMLMotionProps<'img'>
>;

const shuffleSponsors = (sponsors: Sponsor[]) =>
  [...sponsors].sort(() => Math.random() - 0.5);

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '10rem' : '-10rem',
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '10rem' : '-10rem',
    opacity: 0,
  }),
};

const SquarePartnersContent = ({ sponsors }: { sponsors: Sponsor[] }) => {
  const shuffledSponsors = useMemo(() => shuffleSponsors(sponsors), [sponsors]);
  const [[partnerPage, partnerDirection], setPartnerPage] = useState([0, 0]);

  const partnerPaginate = (newPartnerDirection: number) => {
    setPartnerPage([
      (partnerPage + newPartnerDirection + shuffledSponsors.length) %
        shuffledSponsors.length,
      newPartnerDirection,
    ]);
  };

  useInterval(() => {
    partnerPaginate(1);
  }, 2000);

  return (
    <Paper
      style={{
        position: 'absolute',
        overflow: 'hidden',
        right: '2rem',
        top: '2rem',
        width: '14rem',
        height: '14rem',
        padding: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      variant="bordered"
    >
      <AnimatePresence initial={false} custom={partnerDirection}>
        <MotionImg
          style={{
            position: 'absolute',
            display: 'block',
            right: '1rem',
            top: '1rem',
            width: '12rem',
            height: '12rem',
            objectFit: 'contain',
          }}
          key={partnerPage}
          src={shuffledSponsors[partnerPage].logo ?? undefined}
          custom={partnerDirection}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        />
      </AnimatePresence>
    </Paper>
  );
};

export const SquarePartners = ({ clubId }: { clubId: number }) => {
  const getPartnersQuery = useQuery<GetSquarePartnersData>(
    gql`
      query SquarePartners_GetPartners($clubId: Int) {
        sponsors_sponsor(where: { club_id: { _eq: $clubId } }) {
          logo
        }
      }
    `,
    {
      variables: {
        clubId: clubId,
      },
      skip: !clubId,
      context: {
        role: 'anonymous',
      },
    }
  );

  useEffect(() => {
    if (clubId) {
      getPartnersQuery.refetch({
        clubId,
      });
    }
  }, [clubId]);

  if (getPartnersQuery?.data?.sponsors_sponsor.length === 0) {
    return null;
  }

  return (
    <>
      {getPartnersQuery?.data?.sponsors_sponsor && (
        <SquarePartnersContent
          sponsors={getPartnersQuery?.data?.sponsors_sponsor}
        />
      )}
    </>
  );
};
